/**
 * ParallaxBackground — reusable layered background for any scene.
 *
 * Supports image layers, solid color fills, and optional drift animations.
 * All layers use depths within the BACKGROUND range (0 to GAME-1),
 * so they never overlap game content.
 *
 * Usage:
 *   const bg = new ParallaxBackground(scene);
 *   bg.addFill(0x0a0e1a);                           // solid sky
 *   bg.addImage("bg_clouds1", { y: 170, drift: 30 }); // drifting clouds
 *   bg.addImage("bg_mountain1", { x: 220, y: 220 });  // static mountain
 */

import { Depth } from "./Depth";

export interface FillLayerConfig {
  y?: number;
  width?: number;
  height?: number;
  alpha?: number;
}

export interface ImageLayerConfig {
  /** X position (default: scene center) */
  x?: number;
  /** Y position — bottom-anchored (default: scene center) */
  y?: number;
  scaleX?: number;
  scaleY?: number;
  /** Tint color to apply (e.g. 0x1a2744 for dark blue mood) */
  tint?: number;
  alpha?: number;
  /** Horizontal drift in pixels — creates a slow back-and-forth tween */
  drift?: number;
  /** Drift duration in ms (default: 12000) */
  driftDuration?: number;
}

export class ParallaxBackground {
  private scene: any;
  private objects: any[] = [];
  private tweens: any[] = [];
  private layerIndex = 0;
  private sceneW: number;
  private sceneH: number;

  constructor(scene: any) {
    this.scene = scene;
    this.sceneW = scene.sys.game.config.width as number;
    this.sceneH = scene.sys.game.config.height as number;
  }

  /** Fractional depth that stays within BACKGROUND..GAME range. */
  private nextDepth(): number {
    // Use fractional values: 0, 0.1, 0.2, ... up to GAME-0.1
    const d = Depth.BACKGROUND + this.layerIndex * 0.1;
    this.layerIndex++;
    return d;
  }

  /** Add a solid color rectangle layer. */
  addFill(color: number, config: FillLayerConfig = {}): this {
    const w = config.width ?? this.sceneW;
    const h = config.height ?? this.sceneH;
    const y = config.y ?? this.sceneH / 2;
    const alpha = config.alpha ?? 1;

    const rect = this.scene.add.rectangle(this.sceneW / 2, y, w, h, color);
    rect.setAlpha(alpha);
    rect.setDepth(this.nextDepth());
    this.objects.push(rect);
    return this;
  }

  /** Add an image layer. Images are bottom-anchored by default for easy stacking. */
  addImage(key: string, config: ImageLayerConfig = {}): this {
    if (!this.scene.textures.exists(key)) return this;

    const x = config.x ?? this.sceneW / 2;
    const y = config.y ?? this.sceneH / 2;
    const scaleX = config.scaleX ?? 1;
    const scaleY = config.scaleY ?? 1;
    const alpha = config.alpha ?? 1;

    const img = this.scene.add.image(x, y, key);
    img.setScale(scaleX, scaleY);
    img.setAlpha(alpha);
    img.setOrigin(0.5, 1);
    img.setDepth(this.nextDepth());

    if (config.tint !== undefined) {
      img.setTint(config.tint);
    }

    if (config.drift) {
      const tween = this.scene.tweens.add({
        targets: img,
        x: img.x + config.drift,
        duration: config.driftDuration ?? 12000,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
      this.tweens.push(tween);
    }

    this.objects.push(img);
    return this;
  }

  /** Remove all background objects and stop tweens. */
  destroy(): void {
    for (const t of this.tweens) t.stop();
    this.tweens = [];
    for (const obj of this.objects) obj.destroy();
    this.objects = [];
  }
}
